interface ScrollOptions {
  behavior: 'smooth' | 'auto';
  top: number;
}

interface ObserverOptions {
  threshold: number;
  rootMargin: string;
}

export class SmoothScrollNav {
  private navLinks: NodeListOf<HTMLAnchorElement>;
  private sections: NodeListOf<HTMLElement>;
  private menuToggle: HTMLElement | null;
  private navMenu: HTMLElement | null;
  private disclosureMenu: HTMLElement | null;
  private observer: IntersectionObserver | null = null;

  constructor() {
    this.navLinks = document.querySelectorAll('.smooth-link');
    this.sections = document.querySelectorAll('section[id]');
    this.menuToggle = document.getElementById('menuToggle');
    this.disclosureMenu = document.getElementById('mobile-menu');
    this.navMenu = document.getElementById('nav-links');

    this.init();
  }

  private init(): void {
    this.addEventListeners();
    this.updateActiveSection();
    this.observeSections();
    this.enableSmoothScrollBehavior();
  }

  private addEventListeners(): void {
    this.navLinks.forEach((link: HTMLAnchorElement) => {
      link.addEventListener('click', (e: Event) => {
        e.preventDefault();
        const targetId = link.getAttribute('href');
        if (targetId) {
          this.scrollToSection(targetId);
          this.setActiveLink(link);

          this.closeMobileMenu();
        }
      });
    });

    if (this.menuToggle) {
      this.menuToggle.addEventListener('click', () => {
        this.toggleMobileMenu();
      });
    }

    window.addEventListener('scroll', () => {
      this.updateActiveSection();
    });

    document.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;

      if (!target.closest('nav')) {
        this.closeMobileMenu();
      }
    });
  }

  private scrollToSection(targetId: string): void {
    const targetSection = document.querySelector(targetId) as HTMLElement;
    if (targetSection) {
      const offsetTop = targetSection.offsetTop - 120;

      const scrollOptions: ScrollOptions = {
        top: offsetTop,
        behavior: 'smooth',
      };

      window.scrollTo(scrollOptions);
    }
  }

  private setActiveLink(activeLink: HTMLAnchorElement): void {
    this.navLinks.forEach((link: HTMLAnchorElement) => {
      link.classList.remove('active');
    });
    activeLink.classList.add('active');
  }

  private updateActiveSection(): void {
    let currentSection: HTMLElement | null = null;
    let maxVisibleArea = 0;
    const viewportTop = window.scrollY;
    const viewportBottom = viewportTop + window.innerHeight;
    const offset = 100;

    this.sections.forEach((section: HTMLElement) => {
      const sectionTop = section.offsetTop;
      const sectionBottom = sectionTop + section.offsetHeight;

      const visibleTop = Math.max(viewportTop + offset, sectionTop);
      const visibleBottom = Math.min(viewportBottom, sectionBottom);
      const visibleArea = Math.max(0, visibleBottom - visibleTop);

      if (visibleArea > maxVisibleArea) {
        maxVisibleArea = visibleArea;
        currentSection = section;
      }
    });

    if (currentSection) {
      const sectionId = currentSection.getAttribute('id');
      if (sectionId) {
        const correspondingLink = document.querySelector(
          `.smooth-link[href="#${sectionId}"]`
        ) as HTMLAnchorElement;
        if (correspondingLink) {
          this.setActiveLink(correspondingLink);
        }
      }
    }
  }

  private observeSections(): void {
    const observerOptions: ObserverOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    this.sections.forEach((section: HTMLElement) => {
      if (this.observer) {
        this.observer.observe(section);
      }
    });
  }

  private toggleMobileMenu(): void {
    if (this.navMenu) {
      this.navMenu.classList.toggle('active');
    }
  }

  private closeMobileMenu(): void {
    if (this.disclosureMenu) {
      this.disclosureMenu.removeAttribute('open');
    }
  }

  private enableSmoothScrollBehavior(): void {
    document.documentElement.style.scrollBehavior = 'smooth';
  }

  public destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}
